/**
 * 統計セクションの作成・編集ダイアログ。v3.1.4
 *
 * フィールド選択 → グラフ種類選択 → タイトル入力。
 * 設問数と種類でおすすめグラフ種類を提案する。
 */
import type {
  FormAnalyticsChartType,
  FormFieldType,
} from '@goenhub/shared';
import { extractFieldElements } from '@goenhub/shared';

import type { FormRecord } from '@/hooks/useForms';
import { Lightbulb } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input, Label } from '@/components/ui/input';
import { toast } from '@/components/ui/toast';
import { useAuth } from '@/hooks/useAuth';
import {
  useUpsertAnalyticsSection,
  type FormAnalyticsSectionRecord,
} from '@/hooks/useFormAnalytics';

import { CHART_TYPE_LABEL } from './ChartRenderer';

interface Props {
  open: boolean;
  formId: string;
  form: FormRecord;
  existing: FormAnalyticsSectionRecord | null;
  /** 新規作成時の初期 displayOrder。 */
  defaultOrder: number;
  onOpenChange: (open: boolean) => void;
}

const CHART_TYPES: FormAnalyticsChartType[] = [
  'bar',
  'bar_horizontal',
  'bar_stacked',
  'pie',
  'donut',
  'line',
  'area',
  'radar',
  'scatter',
  'metric',
];

const CATEGORICAL = new Set<FormFieldType>([
  'select',
  'radio',
  'checkbox',
  'consent',
  'reply_request',
]);
const NUMERIC = new Set<FormFieldType>(['number']);
const DATE = new Set<FormFieldType>(['date']);

/**
 * 設問数と種類から推奨グラフ種類を導出。
 */
function recommendChartType(
  selectedFields: Array<{ fieldType: FormFieldType }>,
): { recommended: FormAnalyticsChartType; reason: string } | null {
  if (selectedFields.length === 0) return null;
  const types = selectedFields.map((f) => f.fieldType);
  const allCategorical = types.every((t) => CATEGORICAL.has(t));
  const anyNumeric = types.some((t) => NUMERIC.has(t));
  const anyDate = types.some((t) => DATE.has(t));

  if (selectedFields.length === 1) {
    if (anyDate)
      return {
        recommended: 'line',
        reason: '日付項目は時系列の折れ線グラフが見やすいです。',
      };
    if (anyNumeric)
      return {
        recommended: 'bar',
        reason: '数値はヒストグラム (棒グラフ) でばらつきが見えます。',
      };
    if (allCategorical) {
      const opts = (selectedFields[0] as { options?: string[] }).options ?? [];
      if (opts.length <= 5)
        return {
          recommended: 'pie',
          reason: '選択肢が少ないので、構成比のパイチャートが分かりやすいです。',
        };
      return {
        recommended: 'bar',
        reason: '選択肢が多い場合は棒グラフが見やすいです。',
      };
    }
  }

  // 複数問
  if (selectedFields.length >= 2) {
    if (allCategorical)
      return {
        recommended: 'bar_stacked',
        reason: '複数の選択肢設問は積み上げ棒グラフで比較しやすいです。',
      };
    if (anyDate)
      return {
        recommended: 'line',
        reason: '日付を含む場合は折れ線グラフで推移を追えます。',
      };
  }

  // 自由テキスト系のフォールバック (姓・名・住所など、通常一意で集計に向かない)
  const TEXT_TYPES_LOCAL = new Set<FormFieldType>([
    'text',
    'textarea',
    'email',
    'tel',
  ]);
  const allText = types.every((t) => TEXT_TYPES_LOCAL.has(t));
  if (allText) {
    return {
      recommended: 'metric',
      reason:
        '自由テキスト系は値が一意になりやすいため、回答数を表示する数値カードがおすすめです。同じ値が繰り返される場合は棒グラフで頻度を可視化できます。',
    };
  }
  return null;
}

export function AddSectionDialog({
  open,
  formId,
  form,
  existing,
  defaultOrder,
  onOpenChange,
}: Props) {
  const { user } = useAuth();
  const upsert = useUpsertAnalyticsSection(formId);
  const fieldElements = extractFieldElements(form.elements);

  const [title, setTitle] = useState('');
  const [chartType, setChartType] = useState<FormAnalyticsChartType>('bar');
  const [fieldIds, setFieldIds] = useState<string[]>([]);

  useEffect(() => {
    if (open) {
      setTitle(existing?.title ?? '');
      setChartType(existing?.chartType ?? 'bar');
      setFieldIds(existing?.fieldIds ?? []);
    }
  }, [open, existing]);

  const selectedFields = useMemo(
    () => fieldElements.filter((f) => fieldIds.includes(f.id)),
    [fieldElements, fieldIds],
  );

  const recommendation = useMemo(
    () => recommendChartType(selectedFields),
    [selectedFields],
  );

  const handleToggleField = (id: string) => {
    setFieldIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const handleSubmit = async () => {
    if (fieldIds.length === 0) {
      toast.error('対象の設問を1つ以上選択してください');
      return;
    }
    if (!user?.uid) return;
    try {
      await upsert.mutateAsync({
        id: existing?.id,
        title: title.trim() || null,
        chartType,
        fieldIds,
        displayOrder: existing?.displayOrder ?? defaultOrder,
        uid: user.uid,
      });
      toast.success(existing ? '更新しました' : '追加しました');
      onOpenChange(false);
    } catch (err) {
      toast.error(`失敗: ${err instanceof Error ? err.message : String(err)}`);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {existing ? '統計セクションを編集' : '統計セクションを追加'}
          </DialogTitle>
          <DialogDescription>
            対象の設問を選んでグラフ種類を選択します。複数設問を選ぶと比較グラフになります。
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5">
          <div>
            <Label htmlFor="sec-title">セクションタイトル (任意)</Label>
            <Input
              id="sec-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="例: 顧客満足度の分布"
            />
            <p className="text-xs text-[var(--text-muted)] mt-1">
              空欄の場合はグラフ種類のラベルが表示されます。
            </p>
          </div>

          <div>
            <Label>対象の設問 (複数選択可)</Label>
            <div className="space-y-1.5 max-h-48 overflow-y-auto rounded-md border border-[var(--border)] p-2">
              {fieldElements.length === 0 ? (
                <p className="text-xs text-[var(--text-muted)] italic p-2">
                  設問が定義されていません。
                </p>
              ) : (
                fieldElements.map((f) => (
                  <label
                    key={f.id}
                    className="flex items-start gap-2 cursor-pointer hover:bg-[var(--bg-hover)] rounded-sm px-2 py-1"
                  >
                    <input
                      type="checkbox"
                      checked={fieldIds.includes(f.id)}
                      onChange={() => handleToggleField(f.id)}
                      className="mt-0.5"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-[var(--text-primary)] truncate">
                        {f.label}
                      </div>
                      <div className="text-[10px] text-[var(--text-muted)] uppercase tracking-wider">
                        {f.fieldType}
                      </div>
                    </div>
                  </label>
                ))
              )}
            </div>
          </div>

          {recommendation && (
            <div className="rounded-md border border-[var(--accent-amber)] bg-[var(--accent-amber-subtle)] px-3 py-2 text-xs flex items-start gap-2">
              <Lightbulb className="size-4 text-[var(--accent-amber)] flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <div className="font-medium text-[var(--accent-amber)]">
                  おすすめ: {CHART_TYPE_LABEL[recommendation.recommended]}
                </div>
                <div className="text-[var(--text-secondary)] mt-0.5">
                  {recommendation.reason}
                </div>
              </div>
              {chartType !== recommendation.recommended && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setChartType(recommendation.recommended)}
                >
                  適用
                </Button>
              )}
            </div>
          )}

          <div>
            <Label>グラフ種類</Label>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-1.5">
              {CHART_TYPES.map((t) => (
                <button
                  key={t}
                  type="button"
                  onClick={() => setChartType(t)}
                  className={`px-2 py-2 rounded-sm border text-xs transition-colors ${
                    chartType === t
                      ? 'bg-[var(--accent)] text-white border-[var(--accent)]'
                      : 'bg-[var(--bg-elevated)] border-[var(--border)] text-[var(--text-secondary)] hover:border-[var(--text-muted)]'
                  }`}
                >
                  {CHART_TYPE_LABEL[t]}
                </button>
              ))}
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={upsert.isPending}
          >
            キャンセル
          </Button>
          <Button
            type="button"
            variant="primary"
            onClick={handleSubmit}
            disabled={upsert.isPending || fieldIds.length === 0}
          >
            {upsert.isPending ? '保存中…' : existing ? '更新' : '追加'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
